import React from 'react'
import * as Accordion from '@radix-ui/react-accordion'
import { ChevronDownIcon } from '@radix-ui/react-icons'

import { Rule } from '../utils/validateForm'

import InputField from './InputField'
import { SelectField } from './SelectField'
import InputTextArea from './InputTextArea'

interface Field {
  name: string
  label: string
  type: 'text' | 'number' | 'select' | 'textarea' | 'email' // Added 'email' type
  options?: Record<string, string> | { key: string; value: string }[]
  keyProp?: string
  valueProp?: string
  validation?: Rule
}

interface Section {
  id: string
  title: string
  fields: Field[]
}

interface FoldableFormProps {
  sections: Section[]
  formData: { [key: string]: any } // More flexible typing to accept various object types
  formErrors: Record<string, string>
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
}

const FoldableForm: React.FC<FoldableFormProps> = ({ sections, formData, formErrors, onInputChange }) => {
  return (
    <Accordion.Root type="multiple" defaultValue={sections.map((s) => s.id)} className="w-full rounded-md shadow-sm">
      {sections.map((section) => (
        <Accordion.Item key={section.id} value={section.id} className="border-b">
          <Accordion.Header>
            <Accordion.Trigger className="group flex h-[45px] flex-1 w-full cursor-pointer items-center justify-between bg-gray-50 px-5 text-[15px] font-medium leading-none text-gray-800 outline-none hover:bg-gray-100">
              {section.title}
              <ChevronDownIcon
                className="text-gray-600 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=open]:rotate-180"
                aria-hidden
              />
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content className="overflow-hidden bg-white text-[15px] data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
            <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
              {section.fields.map((field) => {
                const error = formErrors[field.name]

                if (field.type === 'select') {
                  return (
                    <SelectField
                      key={field.name}
                      label={field.label}
                      name={field.name}
                      value={(formData[field.name] || '').toString()}
                      options={(field.options || {}) as any}
                      handleInputChange={onInputChange}
                      errors={error ? { [field.name]: error } : {}}
                      keyProp={field.keyProp}
                      valueProp={field.valueProp}
                    />
                  )
                }

                if (field.type === 'textarea') {
                  return (
                    <div className="md:col-span-3">
                      <InputTextArea
                        key={field.name}
                        label={field.label}
                        name={field.name}
                        value={(formData[field.name] || '').toString()}
                        handleInputChange={onInputChange}
                        errors={error ? { [field.name]: error } : {}}
                      />
                    </div>
                  )
                }

                return (
                  <InputField
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    value={(formData[field.name] || '').toString()}
                    type={field.type}
                    handleInputChange={onInputChange}
                    errors={error ? { [field.name]: error } : {}}
                  />
                )
              })}
            </div>
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  )
}

export default FoldableForm
